/**
 * The audio front end.
 *
 * Owns the AudioContext lifecycle (lazily created, resumed on the first user
 * gesture, suspended when the tab is hidden), mute/volume persistence, and one
 * method per cue. Bonus cues are batched inside a microtask so that a play which
 * scores a wild, a 21, a five-card and a streak at once arpeggiates in a fixed
 * musical order and ducks the card layer, instead of four sounds landing on the
 * same sample and turning to mud.
 *
 * Typical wiring:
 *
 *   const audio = new Audio();
 *   const unbind = audio.bind(bus);           // translates game events to cues
 *   startButton.addEventListener('click', () => { void audio.unlock(); });
 */

import { bus as sharedBus, type EventBus } from '@/core/bus';
import { motion } from '@/core/tokens';
import { PILE_COUNT, type BonusKind } from '@/game/types';
import { DESIGN, PILES, pilePosition } from '@/pixi/layout';
import { MusicPlayer, type MusicTrack } from '@/audio/music';
import { SampleKit, type SampleName } from '@/audio/samples';
import {
  LEVEL_UP_SEQUENCE,
  RANK_UP_SEQUENCE,
  ceremonyKit,
  chestCues,
  coinLandCues,
  type ChestBeat,
} from '@/audio/ceremony-kit';
import * as sfx from '@/audio/sfx';
import { SynthEngine, clamp } from '@/audio/synth';

const STORE_MUTED = 'wild21.audio.muted';
const STORE_VOLUME = 'wild21.audio.volume';
const STORE_MUSIC = 'wild21.audio.music';
const STORE_SFX = 'wild21.audio.sfx';
const STORE_SFX_MUTED = 'wild21.audio.sfxMuted';
const STORE_MUSIC_MUTED = 'wild21.audio.musicMuted';

/** Headroom below unity so a full bonus stack still has somewhere to go. */
const MASTER_TRIM = 1;

/** Gap between stacked bonus cues. Long enough to separate, short enough to read as one gesture. */
const BONUS_STAGGER = 0.085;

/** How wide the table is in the stereo field. Subtle — this is a 2.5D table, not a soundstage. */
const PAN_WIDTH = 0.34;

/**
 * Seconds left on the clock at which the bed switches to the tension track.
 * Matches the countdown cue in `bind()`, so the music and the ticking arrive
 * together rather than as two unrelated warnings.
 */
const TENSION_AT_SECONDS = 15;

/** How hard a big cue pulls the music down, in dB, and for how long. */
const MUSIC_DUCK = {
  bonus: { db: 4, hold: 0.25 },
  bust: { db: 6, hold: 0.35 },
  over: { db: 9, hold: 1.6 },
} as const;

/** Fixed play order for stacked bonuses: the rescue, then the score, then the payout, then the ladder. */
const BONUS_ORDER: Record<BonusKind | 'streak', number> = {
  wild: 0,
  twentyOne: 1,
  fiveCard: 2,
  streak: 3,
};

/** Minimum seconds between retriggers of the same cue, to stop double-fires. */
const GATES: Record<string, number> = {
  wild: 0.05,
  twentyOne: 0.05,
  fiveCard: 0.05,
  streak: 0.05,
  bust: 0.12,
  gameOver: 0.6,
  perfectGame: 0.6,
  shuffle: 0.3,
  tickWarning: 0.35,
  uiHover: 0.04,
  uiPress: 0.04,
};

export interface AudioOptions {
  /** Start muted. Defaults to the persisted value, else false. */
  muted?: boolean;
  /** 0..1. Defaults to the persisted value, else 0.8. */
  volume?: number;
  /** Suspend the context while the tab is hidden. Default true. */
  pauseWhenHidden?: boolean;
  /** Simultaneous voice cap; the oldest voice is stolen past this. Default 32. */
  maxVoices?: number;
  /** Persist mute/volume to localStorage. Default true. */
  persist?: boolean;
  /**
   * Use the generated one-shots in `public/audio/sfx/`. Default true. When a
   * sample is missing or still loading the synth covers that cue, so turning
   * this off is a clean A/B against the original synthesised sound design.
   */
  samples?: boolean;
  /** Play background music. Default true. */
  music?: boolean;
  /** Music level, 0..1, independent of the master volume. Defaults to the persisted value, else 0.6. */
  musicVolume?: number;
  /**
   * Effects level, 0..1, independent of the master volume and of the music.
   *
   * The reason this exists separately from `volume`: the music beds join the
   * graph post-limiter, so `volume` is the only fader both layers share. A
   * player who wants the bed and not the card sounds — or who is playing over
   * their own music and wants the table quiet — could not previously express
   * that. Defaults to the persisted value, else 1.
   */
  sfxVolume?: number;
  /** Start with effects muted. Defaults to the persisted value, else false. */
  sfxMuted?: boolean;
  /** Start with music muted. Defaults to the persisted value, else false. */
  musicMuted?: boolean;
  /** URL prefix for the audio assets. Defaults to `audio/`, relative to the page. */
  assetBase?: string;
  /**
   * Play through the iOS **playback** audio session rather than the ambient
   * one, so the ringer switch does not silence the game. Default true, and a
   * no-op off iOS. See {@link Audio.unlock}.
   */
  iosPlaybackSession?: boolean;
}

export interface PlayOptions {
  /** Seconds from now, scheduled on the audio clock. */
  delay?: number;
  /** Linear level scale, 1 = as designed. */
  level?: number;
  /** Pile index 0..3 — places the cue in the stereo field at that pile. */
  pile?: number;
  /** Explicit pan, -1..1. Overrides `pile`. */
  pan?: number;
}

export interface BindOptions {
  /**
   * Play deal/land sounds from `card:play` events. Turn this off if the
   * choreographer drives `cardDeal()` / `cardLand()` on its own timeline.
   */
  cards?: boolean;
  /** Play the riffle on `game:start`. Default true. */
  shuffle?: boolean;
  /**
   * Drive the music beds from game events: `play` on start, `tension` in the
   * closing seconds, `menu` after the round is paid off. Default true.
   */
  music?: boolean;
}

interface PendingBonus {
  order: number;
  play: (when: number, scale: number) => void;
}

/**
 * Is this WebKit on an Apple touch device?
 *
 * iPadOS reports itself as a Mac, so the platform string alone is not enough —
 * a Mac with a touch screen does not exist, which makes `maxTouchPoints` the
 * distinguishing fact.
 */
export function isAppleTouchDevice(): boolean {
  const nav = globalThis.navigator;
  if (!nav) return false;
  if (/iPad|iPhone|iPod/.test(nav.userAgent)) return true;
  return nav.platform === 'MacIntel' && nav.maxTouchPoints > 1;
}

/**
 * A short run of true digital silence, as a WAV blob.
 *
 * Built rather than embedded so the source stays readable: 16-bit mono at 8kHz
 * is about 8KB for half a second, and every sample is zero.
 */
export function silentWavBytes(seconds = 0.5, rate = 8000): ArrayBuffer {
  const frames = Math.max(1, Math.floor(seconds * rate));
  const size = 44 + frames * 2;
  const buffer = new ArrayBuffer(size);
  const view = new DataView(buffer);
  const ascii = (at: number, text: string): void => {
    for (let i = 0; i < text.length; i++) view.setUint8(at + i, text.charCodeAt(i));
  };
  ascii(0, 'RIFF');
  view.setUint32(4, size - 8, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM header length
  view.setUint16(20, 1, true); // format: PCM
  view.setUint16(22, 1, true); // channels: mono
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  ascii(36, 'data');
  view.setUint32(40, frames * 2, true);
  // The samples are already zero, which is silence for signed 16-bit PCM.
  return buffer;
}

function silentWavUrl(seconds = 0.5, rate = 8000): string {
  return URL.createObjectURL(new Blob([silentWavBytes(seconds, rate)], { type: 'audio/wav' }));
}

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  const globals = globalThis as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return globals.AudioContext ?? globals.webkitAudioContext ?? null;
}

function readStore(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStore(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* private mode / disabled storage — mute simply will not persist */
  }
}

/**
 * Stereo position of a pile, derived from the table layout rather than guessed.
 *
 * `@/pixi/layout` puts the origin at the top-left of the design rect, not at the
 * middle of the table the way the 3D branch did, so the pile's x has to be taken
 * relative to the design centre before it means anything as a pan.
 */
function pilePan(pile: number): number {
  const index = clamp(Math.round(pile), 0, PILE_COUNT - 1);
  const extent = (PILES.spacing * (PILE_COUNT - 1)) / 2;
  if (extent <= 0) return 0;
  const offset = pilePosition(index).x - DESIGN.width / 2;
  return clamp((offset / extent) * PAN_WIDTH, -1, 1);
}

/**
 * All of the game's sound. Safe to construct before any user gesture: nothing
 * touches the Web Audio API until {@link Audio.init} or {@link Audio.unlock}
 * runs, and cues are dropped silently while the context is not running.
 */
export class Audio {
  #engine: SynthEngine | null = null;
  #muted: boolean;
  #volume: number;
  #pauseWhenHidden: boolean;
  #maxVoices: number;
  #persist: boolean;
  #pending: PendingBonus[] = [];
  #flushQueued = false;
  #lastPlayed = new Map<string, number>();
  #gestureArmed = false;
  #disposed = false;
  readonly #usePlaybackSession: boolean = true;
  #session: HTMLAudioElement | null = null;
  #sessionUrl: string | null = null;
  #samples: SampleKit | null = null;
  #music: MusicPlayer | null = null;
  #useSamples: boolean;
  #useMusic: boolean;
  #musicVolume: number;
  #sfxVolume: number;
  #sfxMuted: boolean;
  #musicMuted: boolean;
  #assetBase: string;
  /** Pending "bring the menu bed back up after the end sting" timer. */
  #musicTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: AudioOptions = {}) {
    this.#persist = options.persist ?? true;
    const storedMuted = this.#persist ? readStore(STORE_MUTED) : null;
    const storedVolume = this.#persist ? readStore(STORE_VOLUME) : null;
    const storedMusic = this.#persist ? readStore(STORE_MUSIC) : null;
    const storedSfx = this.#persist ? readStore(STORE_SFX) : null;
    const parsedVolume = storedVolume === null ? Number.NaN : Number.parseFloat(storedVolume);
    const parsedMusic = storedMusic === null ? Number.NaN : Number.parseFloat(storedMusic);
    const parsedSfx = storedSfx === null ? Number.NaN : Number.parseFloat(storedSfx);

    this.#muted = options.muted ?? storedMuted === '1';
    this.#volume = clamp(options.volume ?? (Number.isFinite(parsedVolume) ? parsedVolume : 0.8), 0, 1);
    this.#pauseWhenHidden = options.pauseWhenHidden ?? true;
    this.#maxVoices = options.maxVoices ?? 32;
    this.#useSamples = options.samples ?? true;
    this.#useMusic = options.music ?? true;
    this.#musicVolume = clamp(
      options.musicVolume ?? (Number.isFinite(parsedMusic) ? parsedMusic : 0.6),
      0,
      1,
    );
    this.#sfxVolume = clamp(
      options.sfxVolume ?? (Number.isFinite(parsedSfx) ? parsedSfx : 1),
      0,
      1,
    );
    this.#sfxMuted = options.sfxMuted
      ?? (this.#persist ? readStore(STORE_SFX_MUTED) === '1' : false);
    this.#musicMuted = options.musicMuted
      ?? (this.#persist ? readStore(STORE_MUSIC_MUTED) === '1' : false);
    this.#assetBase = options.assetBase ?? 'audio/';
    this.#usePlaybackSession = options.iosPlaybackSession ?? true;
  }

  /* ------------------------------------------------------------ lifecycle -- */

  /**
   * Create the audio graph if it does not exist yet and ask the browser to
   * resume it. Cheap and idempotent — call it whenever, but note that browsers
   * only honour the resume inside a user gesture, so also call {@link unlock}
   * from a click handler.
   */
  init(): boolean {
    if (this.#disposed) return false;
    if (this.#engine) {
      this.#tryResume();
      return true;
    }
    const Ctor = audioContextCtor();
    if (!Ctor) return false;

    let ctx: AudioContext;
    try {
      ctx = new Ctor({ latencyHint: 'interactive' });
    } catch {
      return false;
    }

    this.#engine = new SynthEngine(ctx, { maxVoices: this.#maxVoices });
    this.#engine.setMasterGain(this.#targetGain(), 0.001);
    this.#engine.setSfxGain(this.#targetSfxGain(), 0.001);

    // Both asset layers load in the background and neither can fail loudly: a
    // missing one-shot falls back to the synth, and missing music is simply
    // silence. Nothing here is awaited, so the first cue is never delayed by a
    // download — early cues just come from the synth.
    if (this.#useSamples) {
      this.#samples = new SampleKit();
      void this.#samples.load(ctx, `${this.#assetBase}sfx/`);
    }
    if (this.#useMusic) {
      // Post-limiter on purpose: the beds are already loudness-normalised and
      // must not be pumped by the compressor reacting to a bonus stack.
      this.#music = new MusicPlayer(ctx, this.#engine.master, {
        base: `${this.#assetBase}music/`,
        level: this.#targetMusicLevel(),
      });
    }

    ctx.addEventListener('statechange', this.#onStateChange);
    if (this.#pauseWhenHidden && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.#onVisibility);
    }
    this.#armGesture();
    this.#tryResume();
    return true;
  }

  /**
   * Call this from a real user gesture (the start button, the first tap). It
   * creates the context if needed, resumes it, and plays a zero-length buffer,
   * which is what actually satisfies iOS.
   */
  async unlock(): Promise<void> {
    if (!this.init()) return;
    const engine = this.#engine;
    if (!engine) return;
    // Before the await, so it is unambiguously inside the gesture.
    this.#promotePlaybackSession();
    try {
      await engine.ctx.resume();
    } catch {
      /* the next gesture will try again */
    }
    try {
      const source = engine.ctx.createBufferSource();
      source.buffer = engine.ctx.createBuffer(1, 1, engine.ctx.sampleRate);
      source.connect(engine.ctx.destination);
      source.start(0);
    } catch {
      /* unlocking is best-effort */
    }
    // Only stop listening once everything that needs a gesture has had one —
    // the context *and* the playback session.
    if (!this.#needsGesture()) this.#disarmGesture();
  }

  /** True once the context exists and is actually running. */
  get ready(): boolean {
    return this.#engine !== null && this.#engine.ctx.state === 'running';
  }

  get context(): AudioContext | null {
    return this.#engine?.ctx ?? null;
  }

  get muted(): boolean {
    return this.#muted;
  }

  get volume(): number {
    return this.#volume;
  }

  setMuted(muted: boolean): void {
    if (this.#muted === muted) return;
    this.#muted = muted;
    if (this.#persist) writeStore(STORE_MUTED, muted ? '1' : '0');
    this.#engine?.setMasterGain(this.#targetGain(), muted ? 0.04 : 0.12);
    if (muted) this.#engine?.panic();
  }

  /** Returns the new mute state. */
  toggleMute(): boolean {
    this.setMuted(!this.#muted);
    return this.#muted;
  }

  /** 0..1. Persisted alongside the mute state. */
  setVolume(volume: number): void {
    const next = clamp(volume, 0, 1);
    if (next === this.#volume) return;
    this.#volume = next;
    if (this.#persist) writeStore(STORE_VOLUME, next.toFixed(3));
    this.#engine?.setMasterGain(this.#targetGain(), 0.08);
  }

  /* ---------------------------------------------------------------- music -- */

  /** Music level, 0..1, independent of the master volume. Persisted. */
  get musicVolume(): number {
    return this.#musicVolume;
  }

  setMusicVolume(level: number): void {
    const next = clamp(level, 0, 1);
    if (next === this.#musicVolume) return;
    this.#musicVolume = next;
    if (this.#persist) writeStore(STORE_MUSIC, next.toFixed(3));
    this.#music?.setLevel(this.#targetMusicLevel());
  }

  /** Is the music silenced, independent of its level and of the master mute? */
  get musicMuted(): boolean {
    return this.#musicMuted;
  }

  setMusicMuted(muted: boolean): void {
    if (this.#musicMuted === muted) return;
    this.#musicMuted = muted;
    if (this.#persist) writeStore(STORE_MUSIC_MUTED, muted ? '1' : '0');
    this.#music?.setLevel(this.#targetMusicLevel());
  }

  /** Returns the new music mute state. */
  toggleMusicMuted(): boolean {
    this.setMusicMuted(!this.#musicMuted);
    return this.#musicMuted;
  }

  /* ------------------------------------------------------------------ sfx -- */

  /**
   * Effects level, 0..1, independent of the master volume and of the music.
   * Persisted.
   */
  get sfxVolume(): number {
    return this.#sfxVolume;
  }

  setSfxVolume(level: number): void {
    const next = clamp(level, 0, 1);
    if (next === this.#sfxVolume) return;
    this.#sfxVolume = next;
    if (this.#persist) writeStore(STORE_SFX, next.toFixed(3));
    this.#engine?.setSfxGain(this.#targetSfxGain(), 0.08);
  }

  /** Is the effects layer silenced, independent of its level and of the master mute? */
  get sfxMuted(): boolean {
    return this.#sfxMuted;
  }

  setSfxMuted(muted: boolean): void {
    if (this.#sfxMuted === muted) return;
    this.#sfxMuted = muted;
    if (this.#persist) writeStore(STORE_SFX_MUTED, muted ? '1' : '0');
    // Faster down than up, like the master mute: silencing should feel
    // immediate, un-silencing should not be a click.
    this.#engine?.setSfxGain(this.#targetSfxGain(), muted ? 0.04 : 0.12);
  }

  /** Returns the new effects mute state. */
  toggleSfxMuted(): boolean {
    this.setSfxMuted(!this.#sfxMuted);
    return this.#sfxMuted;
  }

  /** The bed currently playing, if any. */
  get musicTrack(): MusicTrack | null {
    return this.#music?.track ?? null;
  }

  /**
   * Crossfade to a bed. Safe to call repeatedly with the same track, and safe
   * before the context exists — it simply does nothing until {@link init}.
   */
  playMusic(track: MusicTrack, fade?: number): void {
    // Whatever was scheduled is now stale. `game:over` queues the menu bed to
    // come up under the breakdown a couple of seconds later, and a player who
    // presses "play again" before then would otherwise start a round and have
    // that timer arrive on top of it — the round's bed replaced by the title
    // screen's, for the rest of the round. An explicit request always wins
    // over a queued one.
    this.#clearMusicTimer();
    void this.#music?.play(track, fade);
  }

  stopMusic(fade?: number): void {
    this.#clearMusicTimer();
    this.#music?.stop(fade);
  }

  /** Hold the bed back behind a modal (the pause panel), or release it. */
  setMusicDim(dimmed: boolean): void {
    this.#music?.setDim(dimmed);
  }

  /**
   * Warm the music cache without playing anything, so the first crossfade of a
   * round does not wait on a download. Call from the start screen.
   */
  preloadMusic(...tracks: MusicTrack[]): void {
    // `menu` is deliberately absent from the default.
    //
    // Nothing plays it any more — music belongs to a round — so fetching it on
    // the first gesture would be downloading a track on a connection the player
    // is about to need for the two that do play. It stays in `MusicTrack` and
    // can still be asked for by name, because the file is there and a future
    // screen may want it.
    const wanted = tracks.length > 0 ? tracks : (['play', 'tension'] as MusicTrack[]);
    for (const track of wanted) void this.#music?.load(track);
  }

  /** Diagnostics: how many one-shots are sampled vs fallen back to the synth. */
  get sampleStatus(): { loaded: number; failed: number; complete: boolean } {
    const kit = this.#samples;
    return {
      loaded: kit?.loaded ?? 0,
      failed: kit?.failed ?? 0,
      complete: kit?.complete ?? false,
    };
  }

  /** Tear everything down. The instance is not reusable afterwards. */
  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#pending.length = 0;
    this.#clearMusicTimer();
    this.#disarmGesture();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.#onVisibility);
    }
    // Let the playback session go, so the player's own audio can come back.
    if (this.#session) {
      this.#session.pause();
      this.#session.remove();
      this.#session = null;
    }
    if (this.#sessionUrl) {
      URL.revokeObjectURL(this.#sessionUrl);
      this.#sessionUrl = null;
    }
    this.#music?.dispose();
    this.#music = null;
    this.#samples?.dispose();
    this.#samples = null;
    const engine = this.#engine;
    this.#engine = null;
    if (engine) {
      engine.ctx.removeEventListener('statechange', this.#onStateChange);
      engine.dispose();
    }
  }

  /* ------------------------------------------------------------------ bus -- */

  /**
   * Subscribe to the game bus and translate events into cues. Returns an
   * unsubscribe function.
   */
  bind(source: EventBus = sharedBus, options: BindOptions = {}): () => void {
    const withCards = options.cards ?? true;
    const withShuffle = options.shuffle ?? true;
    const offs: Array<() => void> = [];

    if (withShuffle) {
      offs.push(source.on('game:start', () => this.shuffle()));
    }

    if (options.music ?? true) {
      offs.push(
        source.on('game:start', () => {
          // Under the riffle, not on top of it: the shuffle is the round's
          // first beat and the bed should arrive behind it.
          this.playMusic('play', 2.2);
        }),
      );

      offs.push(
        source.on('tick', (event) => {
          // Idempotent — `play()` ignores a request for the bed already
          // running, so driving a track change from a 1Hz event is safe.
          if (event.secondsRemaining > 0 && event.secondsRemaining <= TENSION_AT_SECONDS) {
            this.playMusic('tension', 1.2);
          }
        }),
      );

      offs.push(
        source.on('game:over', () => {
          // The round is over, so the music is over.
          //
          // The menu bed used to come back up under the score breakdown, which
          // meant it then carried on through the menu and every screen after
          // it — the player finishes a round and the game keeps playing at
          // them while they read their history. Music belongs to a round.
          // Silence between rounds is not an absence; it is the end of one.
          this.stopMusic(1);
          // And the next round's bed is fetched now, during the results, so the
          // riffle does not wait on a download (a round takes one of six tracks).
          void this.#music?.preloadNext();
        }),
      );
    }

    if (withCards) {
      offs.push(
        source.on('card:play', (event) => {
          this.cardDeal({ pile: event.pile });
          // The card is in the air for `motion.deal`; land just before it settles.
          this.cardLand({ pile: event.pile, delay: motion.deal * 0.82 });
        }),
      );
    }

    offs.push(
      source.on('pile:resolve', (event) => {
        const { resolution } = event;
        // A bust arrives on its own event; do not double-fire it here.
        if (resolution.isBust) return;
        for (const kind of resolution.bonuses) this.#bonusCue(kind, { pile: resolution.pile });
        if (resolution.streakLevel > 0) this.streak(resolution.streakLevel);
      }),
    );

    offs.push(
      source.on('level:up', (event) => {
        // 80 ms behind the event, so the chime arrives under the banner's
        // leading edge rather than on the same frame as the resolve cue that
        // preceded it. Two cues on one frame read as one muddier cue.
        this.levelUp(event.level, { delay: 0.08 });
      }),
    );

    offs.push(source.on('bust', (event) => this.bust({ pile: event.pile })));

    offs.push(
      source.on('tick', (event) => {
        const left = event.secondsRemaining;
        if (left > 0 && left <= 15) this.tickWarning(left);
      }),
    );

    offs.push(source.on('undo', () => this.undo()));

    offs.push(
      source.on('game:over', (event) => {
        if (event.final.perfectGame) this.perfectGame();
        else this.gameOver();
      }),
    );

    return () => {
      for (const off of offs) off();
      offs.length = 0;
      this.#clearMusicTimer();
    };
  }

  #clearMusicTimer(): void {
    if (this.#musicTimer === null) return;
    clearTimeout(this.#musicTimer);
    this.#musicTimer = null;
  }

  /* ----------------------------------------------------------------- cues -- */

  cardDeal(options: PlayOptions = {}): void {
    this.#play('cardDeal', options, (bus, cue) => sfx.cardDeal(bus, cue), 'cardDeal');
  }

  cardLand(options: PlayOptions = {}): void {
    this.#play('cardLand', options, (bus, cue) => sfx.cardLand(bus, cue), 'cardLand');
  }

  cardFlip(options: PlayOptions = {}): void {
    this.#play('cardFlip', options, (bus, cue) => sfx.cardFlip(bus, cue), 'cardFlip');
  }

  twentyOne(options: PlayOptions = {}): void {
    this.#queueBonus(
      'twentyOne',
      options,
      (bus, cue) => sfx.twentyOne(bus, cue),
      (bus, cue) => this.#sample(bus, 'twentyOne', cue),
    );
  }

  fiveCard(options: PlayOptions = {}): void {
    this.#queueBonus(
      'fiveCard',
      options,
      (bus, cue) => sfx.fiveCard(bus, cue),
      (bus, cue) => this.#sample(bus, 'fiveCard', cue),
    );
  }

  wild(options: PlayOptions = {}): void {
    this.#queueBonus(
      'wild',
      options,
      (bus, cue) => sfx.wild(bus, cue),
      (bus, cue) => this.#sample(bus, 'wild', cue),
    );
  }

  /**
   * Streak level 1..5, i.e. the 2x..6x callouts.
   *
   * The sampled ladder is one recording replayed at just-intonation ratios —
   * see `STREAK_RATIOS` in `./samples.ts` for why that beats five separate
   * generations.
   */
  streak(streakLevel: number, options: PlayOptions = {}): void {
    this.#queueBonus(
      'streak',
      options,
      (bus, cue) => sfx.streak(bus, streakLevel, cue),
      (bus, cue) => {
        const kit = this.#samples;
        if (!kit) return false;
        return kit.playStreak(bus, streakLevel, {
          ...(cue.when === undefined ? {} : { when: cue.when }),
          ...(cue.level === undefined ? {} : { level: cue.level }),
          ...(cue.pan === undefined ? {} : { pan: cue.pan }),
        });
      },
    );
  }

  /**
   * A level was reached. Cool, quiet, and deliberately not a scoring cue.
   *
   * Not routed through `#queueBonus`: that queue orders the cues that a single
   * resolution can fire together, so that a 21 and a five-card do not collide.
   * A level-up is not one of those — it is a separate beat on its own clock,
   * and joining the queue would make it fight for a slot with the very cues it
   * is supposed to arrive after.
   */
  levelUp(level: number, options: PlayOptions = {}): void {
    const engine = this.#audible();
    if (!engine) return;
    const milestone = level % 5 === 0;
    const cue = this.#cue(options, this.#at(engine, options), 1);
    sfx.levelUp(engine, milestone, cue);
  }

  bust(options: PlayOptions = {}): void {
    const engine = this.#audible();
    if (!engine) return;
    if (!this.#gate('bust', engine.ctx.currentTime)) return;
    engine.duck('card', 2, 0.28);
    this.#music?.duck(MUSIC_DUCK.bust.db, MUSIC_DUCK.bust.hold);
    const cue = this.#cue(options, this.#at(engine, options), 1);
    if (this.#sample(engine, 'bust', cue)) return;
    sfx.bust(engine, cue);
  }

  /** One soft pulse of the final-fifteen countdown. Pass the seconds left. */
  tickWarning(secondsRemaining: number, options: PlayOptions = {}): void {
    // The synth pitches its tick by how little time is left; the sample cannot,
    // so it leans on rate instead — the same information, carried by tempo of
    // perceived brightness rather than by an authored pitch.
    const urgency = clamp((15 - secondsRemaining) / 15, 0, 1);
    this.#play(
      'tickWarning',
      { ...options, level: (options.level ?? 1) * (0.75 + urgency * 0.45) },
      (bus, cue) => sfx.tickWarning(bus, secondsRemaining, cue),
      'tickWarning',
    );
  }

  gameOver(options: PlayOptions = {}): void {
    const engine = this.#audible();
    if (!engine) return;
    if (!this.#gate('gameOver', engine.ctx.currentTime)) return;
    engine.duck('card', 4, 1.4);
    this.#music?.duck(MUSIC_DUCK.over.db, MUSIC_DUCK.over.hold);
    const cue = this.#cue(options, this.#at(engine, options), 1);
    if (this.#sample(engine, 'gameOver', cue)) return;
    sfx.gameOver(engine, cue);
  }

  /**
   * The rank-up ceremony — Blockari's own cues, at Blockari's own gains and
   * offsets (`audio/ceremony-kit.ts`), because a ceremony has to sound the
   * same in every title.
   *
   * **Off until Adam has heard it**: `src/ceremony/sound.ts` is the only
   * caller and it answers no unless the flag is set. Mixed at −24.7 LUFS,
   * under YOU WON's −23.6.
   */
  rankUp(_rankIndex = 0, options: PlayOptions = {}): void {
    const engine = this.#audible();
    if (!engine) return;
    if (!this.#gate('rankUp', engine.ctx.currentTime)) return;
    this.#music?.duck(MUSIC_DUCK.over.db, MUSIC_DUCK.over.hold);
    void this.#ceremonyCues(engine).then(() => {
      ceremonyKit.sequence(engine, RANK_UP_SEQUENCE, this.#at(engine, options));
    });
  }

  /** The level-up's flip, over the tray: the chime, then the shards as it lands. */
  levelFlip(options: PlayOptions = {}): void {
    const engine = this.#audible();
    if (!engine) return;
    if (!this.#gate('levelFlip', engine.ctx.currentTime)) return;
    void this.#ceremonyCues(engine).then(() => {
      ceremonyKit.sequence(engine, LEVEL_UP_SEQUENCE, this.#at(engine, options));
    });
  }

  /**
   * A chest screen beat. `step` is the rung for the climb and the card's index
   * for a deal, so each one is a little higher than the last.
   */
  chestBeat(beat: ChestBeat, step = 0, options: PlayOptions = {}): void {
    const engine = this.#audible();
    if (!engine) return;
    void this.#ceremonyCues(engine).then(() => {
      ceremonyKit.sequence(engine, chestCues(beat, step), this.#at(engine, options));
    });
  }

  /** One coin of a payout stream landing in the balance pill; `step` of `total`, 1-based. */
  coinLand(step: number, total: number, options: PlayOptions = {}): void {
    const engine = this.#audible();
    if (!engine) return;
    void this.#ceremonyCues(engine).then(() => {
      ceremonyKit.sequence(engine, coinLandCues(step, total), this.#at(engine, options));
    });
  }

  /**
   * Load the vendored cues on first use.
   *
   * Not at boot: these six files are only ever heard on a results screen or a
   * chest, and a player who never crosses a rank should not pay for them.
   * Scheduling after the load means the first ceremony of a session can start
   * a frame or two late; every one after it is immediate.
   */
  #ceremonyCues(engine: SynthEngine): Promise<void> {
    return ceremonyKit.load(engine.ctx, `${this.#assetBase}sfx/`);
  }

  perfectGame(options: PlayOptions = {}): void {
    const engine = this.#audible();
    if (!engine) return;
    if (!this.#gate('perfectGame', engine.ctx.currentTime)) return;
    engine.duck('card', 4, 1.8);
    this.#music?.duck(MUSIC_DUCK.over.db, MUSIC_DUCK.over.hold);
    const cue = this.#cue(options, this.#at(engine, options), 1);
    if (this.#sample(engine, 'perfectGame', cue)) return;
    sfx.perfectGame(engine, cue);
  }

  uiHover(options: PlayOptions = {}): void {
    this.#play('uiHover', options, (bus, cue) => sfx.uiHover(bus, cue), 'uiHover');
  }

  uiPress(options: PlayOptions = {}): void {
    this.#play('uiPress', options, (bus, cue) => sfx.uiPress(bus, cue), 'uiPress');
  }

  /** Deck riffle — the start of a round. */
  shuffle(options: PlayOptions = {}): void {
    this.#play('shuffle', options, (bus, cue) => sfx.shuffle(bus, cue), 'shuffle');
  }

  undo(options: PlayOptions = {}): void {
    this.#play('undo', options, (bus, cue) => sfx.undo(bus, cue), 'undo');
  }

  /* -------------------------------------------------------------- internal -- */

  #bonusCue(kind: BonusKind, options: PlayOptions): void {
    switch (kind) {
      case 'twentyOne':
        this.twentyOne(options);
        break;
      case 'fiveCard':
        this.fiveCard(options);
        break;
      case 'wild':
        this.wild(options);
        break;
    }
  }

  /** Absolute start time for a cue, honouring `delay`. */
  #at(engine: SynthEngine, options: PlayOptions): number {
    return engine.now() + Math.max(options.delay ?? 0, 0);
  }

  /** The engine, but only when a sound would actually be heard. */
  #audible(): SynthEngine | null {
    if (this.#disposed || this.#muted || this.#volume <= 0) return null;
    const engine = this.#engine;
    if (!engine) return null;
    if (engine.ctx.state !== 'running') {
      this.#tryResume();
      return null;
    }
    return engine;
  }

  #targetGain(): number {
    return this.#muted ? 0 : this.#volume * MASTER_TRIM;
  }

  #targetSfxGain(): number {
    return this.#sfxMuted ? 0 : this.#sfxVolume;
  }

  /**
   * The level the music bus should actually run at.
   *
   * `musicVolume` is what the player set; this is what they should hear. They
   * differ only while the music is muted, and keeping them apart is what lets
   * un-muting restore the level rather than a default.
   */
  #targetMusicLevel(): number {
    return this.#musicMuted ? 0 : this.#musicVolume;
  }

  #cue(options: PlayOptions, when: number, scale: number): sfx.CueOptions {
    const pan = options.pan ?? (options.pile === undefined ? undefined : pilePan(options.pile));
    return {
      when,
      level: (options.level ?? 1) * scale,
      ...(pan === undefined ? {} : { pan }),
    };
  }

  #gate(key: string, now: number): boolean {
    const min = GATES[key];
    if (min === undefined) return true;
    const last = this.#lastPlayed.get(key);
    if (last !== undefined && now - last < min) return false;
    this.#lastPlayed.set(key, now);
    return true;
  }

  /**
   * Play a cue from its generated sample if one is loaded.
   *
   * This is the whole sample/synth contract in one place: it returns `false`
   * for a cue with no sample, a sample that has not finished loading, or a
   * sample that failed to load, and every caller treats `false` as "render it
   * with the synth instead". The game therefore degrades to the original
   * synthesised sound design rather than to silence.
   */
  #sample(engine: SynthEngine, name: SampleName, cue: sfx.CueOptions): boolean {
    const kit = this.#samples;
    if (!kit) return false;
    return kit.play(engine, name, {
      ...(cue.when === undefined ? {} : { when: cue.when }),
      ...(cue.level === undefined ? {} : { level: cue.level }),
      ...(cue.pan === undefined ? {} : { pan: cue.pan }),
    });
  }

  #play(
    key: string,
    options: PlayOptions,
    render: (bus: SynthEngine, cue: sfx.CueOptions) => void,
    sample?: SampleName,
  ): void {
    const engine = this.#audible();
    if (!engine) return;
    if (!this.#gate(key, engine.ctx.currentTime)) return;
    const cue = this.#cue(options, this.#at(engine, options), 1);
    if (sample !== undefined && this.#sample(engine, sample, cue)) return;
    render(engine, cue);
  }

  /**
   * Bonus cues are collected for the rest of the current task and played as one
   * staggered phrase. Everything is in the same key, so a four-way stack reads
   * as a single arpeggio rather than four sounds colliding.
   */
  #queueBonus(
    key: keyof typeof BONUS_ORDER,
    options: PlayOptions,
    render: (bus: SynthEngine, cue: sfx.CueOptions) => void,
    sample?: (engine: SynthEngine, cue: sfx.CueOptions) => boolean,
  ): void {
    const engine = this.#audible();
    if (!engine) return;
    if (!this.#gate(key, engine.ctx.currentTime)) return;

    // Duck the bed for the payoff. Done on the way in rather than in the flush,
    // so a four-way stack ducks once for the whole phrase instead of four times.
    this.#music?.duck(MUSIC_DUCK.bonus.db, MUSIC_DUCK.bonus.hold);

    const delay = Math.max(options.delay ?? 0, 0);
    this.#pending.push({
      order: BONUS_ORDER[key],
      play: (when, scale) => {
        const target = this.#audible();
        if (!target) return;
        const cue = this.#cue(options, when + delay, scale);
        if (sample !== undefined && sample(target, cue)) return;
        render(target, cue);
      },
    });

    if (!this.#flushQueued) {
      this.#flushQueued = true;
      queueMicrotask(() => this.#flushBonuses());
    }
  }

  #flushBonuses(): void {
    this.#flushQueued = false;
    const items = this.#pending;
    this.#pending = [];
    if (items.length === 0) return;

    const engine = this.#audible();
    if (!engine) return;

    items.sort((a, b) => a.order - b.order);
    const count = items.length;
    const stagger = count > 1 ? BONUS_STAGGER : 0;
    // Pull the stack down as it grows so four bonuses are not four times as loud.
    const scale = 1 / (1 + 0.2 * (count - 1));
    const start = engine.now();
    for (let i = 0; i < count; i++) items[i]!.play(start + i * stagger, scale);

    engine.duck('card', count > 1 ? 3.5 : 2.2, stagger * count + 0.35);
  }

  #tryResume(): void {
    const engine = this.#engine;
    if (!engine || engine.ctx.state === 'running') return;
    void engine.ctx.resume().catch(() => undefined);
  }

  /**
   * Is there anything left that only a user gesture can fix?
   *
   * Two independent things have to be true for the game to be audible on iOS:
   * the context has to be running, **and** the silent element that holds the
   * playback session has to be playing. Either one can be knocked out on its
   * own, and either one on its own is enough to make the game silent — so both
   * have to be asked about, or a healthy context masks a stalled session.
   */
  #needsGesture(): boolean {
    const engine = this.#engine;
    if (!engine) return false;
    if (engine.ctx.state !== 'running') return true;
    return this.#session !== null && this.#session.paused;
  }

  #onStateChange = (): void => {
    // Safari interrupts the context on phone calls and similar; re-arm the
    // gesture unlock so the next tap brings the sound back.
    if (this.#needsGesture()) this.#armGesture();
  };

  #onVisibility = (): void => {
    const engine = this.#engine;
    if (!engine || typeof document === 'undefined') return;
    if (document.visibilityState === 'hidden') {
      engine.panic();
      // Let the playback session go while the player is elsewhere, so their own
      // audio can come back rather than being held mute by a game they are not
      // looking at.
      this.#session?.pause();
      void engine.ctx.suspend().catch(() => undefined);
      return;
    }

    this.#tryResume();
    this.#resumePlaybackSession();

    // Arm regardless of whether those two worked.
    //
    // Coming back from the home screen, `play()` on the silent element is
    // refused — returning to a tab is not a user gesture — so the session stays
    // ambient and the game is silent even though the context resumed perfectly
    // well. Arming only when the *context* was suspended missed exactly that
    // case, and left no route back at all: sound went away on the first
    // background and never returned. The first tap after this repairs both, and
    // `unlock` disarms once there is genuinely nothing left to repair.
    this.#armGesture();
  };

  #onGesture = (): void => {
    void this.unlock();
  };

  /**
   * Take the iOS audio session out of "ambient" and into "playback".
   *
   * On iOS, Web Audio plays through the *ambient* session, and the ambient
   * session is what the ringer switch silences. So a player with the switch
   * flipped — which on a phone that lives in a pocket is most of them — hears
   * a completely silent game at full volume, with nothing on screen to explain
   * it and no in-page control that can fix it.
   *
   * There is no web API for the session category. The one lever is an
   * `<audio>` **element**: iOS promotes the session to playback while one is
   * playing, and Web Audio then rides along audibly. So this plays half a
   * second of true digital silence on a loop.
   *
   * ### What this costs
   *
   * A playback session is exclusive on iOS, so starting it **stops whatever
   * the player already had playing** — their podcast, their music. That is not
   * a bug in this code and there is no `mixWithOthers` to ask for from a web
   * page; it is the trade the platform offers. It is taken deliberately here
   * because a card game with sound design is not worth shipping mute, and it
   * is confined to iOS: on Android and desktop the ringer switch problem does
   * not exist, so the session is left alone and nobody's music is touched.
   * `iosPlaybackSession: false` opts out.
   *
   * The element is paused with the rest of the audio when the tab hides, so
   * the game does not hold the session while it is not being played.
   */
  #promotePlaybackSession(): void {
    if (!this.#usePlaybackSession || this.#disposed) return;
    if (typeof document === 'undefined' || !isAppleTouchDevice()) return;

    if (!this.#session) {
      const el = document.createElement('audio');
      this.#sessionUrl = silentWavUrl();
      el.src = this.#sessionUrl;
      el.loop = true;
      el.preload = 'auto';
      // Not `muted`: a muted element does not promote the session, which is
      // the whole point. The audio itself is silent instead.
      el.volume = 1;
      el.setAttribute('playsinline', '');
      el.setAttribute('aria-hidden', 'true');
      el.style.display = 'none';
      document.body.append(el);
      this.#session = el;
    }

    // Every gesture, not only the first. `play()` is refused unless the
    // browser agrees the call is inside a real user interaction, and the first
    // gesture of a session is exactly the one most likely to be spent before
    // the page is ready to use it. Creating the element once and then never
    // trying again would turn a single refused call into a permanently silent
    // game — which is the failure this whole method exists to prevent.
    this.#resumePlaybackSession();
  }

  /** Start, or restart, the silent element that holds the session open. */
  #resumePlaybackSession(): void {
    const el = this.#session;
    if (!el || this.#disposed || !el.paused) return;
    void el.play().catch(() => {
      // Refused. The next gesture tries again.
    });
  }

  #armGesture(): void {
    if (this.#gestureArmed || this.#disposed || typeof window === 'undefined') return;
    this.#gestureArmed = true;
    window.addEventListener('pointerdown', this.#onGesture, { passive: true });
    window.addEventListener('touchend', this.#onGesture, { passive: true });
    window.addEventListener('keydown', this.#onGesture);
  }

  #disarmGesture(): void {
    if (!this.#gestureArmed || typeof window === 'undefined') return;
    this.#gestureArmed = false;
    window.removeEventListener('pointerdown', this.#onGesture);
    window.removeEventListener('touchend', this.#onGesture);
    window.removeEventListener('keydown', this.#onGesture);
  }
}

/** Shared instance, mirroring the shared `bus`. Integration may use its own. */
export const audio = new Audio();

export type { SynthBus } from '@/audio/synth';
